import apiService from '@/services/api';

const ESEWA_CONTEXT_KEY = 'ESEWA_PAYMENT_CONTEXT';

export const readEsewaContext = () => {
  try {
    const raw = localStorage.getItem(ESEWA_CONTEXT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearEsewaContext = () => {
  try {
    localStorage.removeItem(ESEWA_CONTEXT_KEY);
  } catch {
    // no-op
  }
};

const resolveAvailabilityId = (plan) => {
  const availabilitySet = Array.isArray(plan?.availabilityset) ? plan.availabilityset : [];
  const fromSet = availabilitySet.find((id) => id !== undefined && id !== null && String(id).trim() !== '');
  if (fromSet) {
    return String(fromSet);
  }
  return String(plan?.availabilityid || plan?.availabilityId || '').trim();
};

const persistEsewaContext = (context) => {
  try {
    localStorage.setItem(ESEWA_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // no-op
  }
};

const pick = (obj, keys, fallback = '') => {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null && obj[key] !== '') {
      return obj[key];
    }
  }
  return fallback;
};

const toEsewaAmount = (value) => {
  const str = String(value ?? '').trim();
  if (!str) return '';
  if (str.includes('.')) return str; // already major unit
  const n = Number(str);
  if (Number.isNaN(n)) return str;
  // Backend subscription prices come in minor units (e.g., 599000). eSewa expects major units.
  // Keep plain numeric string (e.g., 5990) instead of forcing trailing decimals.
  const divided = n / 100;
  return Number.isInteger(divided) ? String(divided) : String(divided);
};

const createAndSubmitEsewaForm = (refData) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = refData.payActionUrl;
  form.style.display = 'none';

  // Prefer exact keys from init response payload.
  const amount = pick(refData, ['amount']);
  const taxAmount = pick(refData, ['taxAmount', 'tax_amount'], 0);
  const totalAmountRaw = pick(refData, ['totalAmount', 'total_amount'], amount);
  const transactionUuid = pick(refData, ['transactionuUuid', 'transaction_uuid', 'transactionUuid']);
  const productCode = pick(refData, ['productCode', 'product_code']);
  const productServiceCharge = pick(refData, ['productServiceCharge', 'product_service_charge'], 0);
  const productDeliveryCharge = pick(refData, ['productDeliveryCharge', 'product_delivery_charge'], 0);
  const signedFieldNames = pick(refData, ['signedFieldNames', 'signed_field_names']);
  const successUrl = pick(refData, ['successUrl', 'success_url'], '');
  const failureUrl = pick(refData, ['failureUrl', 'failure_url'], '');
  const signature = pick(refData, ['signature'], '');

  const fields = {
    amount: toEsewaAmount(amount),
    tax_amount: toEsewaAmount(taxAmount),
    total_amount: toEsewaAmount(totalAmountRaw),
    transaction_uuid: String(transactionUuid || ''),
    product_code: String(productCode || ''),
    product_service_charge: toEsewaAmount(productServiceCharge),
    product_delivery_charge: toEsewaAmount(productDeliveryCharge),
    success_url: String(successUrl),
    failure_url: String(failureUrl),
    signed_field_names: String(signedFieldNames || ''),
    signature: String(signature),
  };

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
};

const resolveCheckoutUrls = () => {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  return {
    successUrl: origin ? `${origin}/payment/success` : '',
    failureUrl: origin ? `${origin}/payment/failed` : '',
  };
};

export const startEsewaSubscriptionCheckout = async (plan, propertyCount) => {
  const availabilityId = resolveAvailabilityId(plan);
  if (!plan?.planId || !availabilityId) {
    throw new Error('Plan details are incomplete for payment.');
  }
  const normalizedPropertyCount = Number(propertyCount);
  if (!Number.isInteger(normalizedPropertyCount) || normalizedPropertyCount <= 0) {
    throw new Error('Property count must be a positive whole number.');
  }

  const { successUrl, failureUrl } = resolveCheckoutUrls();

  const precheckResponse = await apiService.precheckSubscription({
    transactionpurpose: 'SUBSCRIBE',
    planid: plan.planId,
    currency: plan.currency || 'NPR',
    propertycount: normalizedPropertyCount,
  });
  const finalAmount = precheckResponse?.data?.finalamount;
  if (finalAmount === undefined || finalAmount === null || finalAmount === '') {
    throw new Error('Invalid precheck response: missing final amount.');
  }

  const response = await apiService.initEsewaPayment({
    devicetype: 'WEB',
    amount: finalAmount,
    currency: plan.currency || 'NPR',
    transactionpurpose: 'SUBSCRIPTION',
    transactionmode: 'WA',
    planid: plan.planId,
    availabilityid: availabilityId,
    propertycount: normalizedPropertyCount,
    successUrl,
    failureUrl,
  });

  const referenceid = response?.data?.referenceid;
  const referencedata = response?.data?.referencedata;

  if (!referenceid || !referencedata?.payActionUrl) {
    throw new Error('Invalid payment init response from server.');
  }

  persistEsewaContext({
    referenceid,
    referencedata,
    propertycount: String(normalizedPropertyCount),
    checkoutpurpose: 'SUBSCRIPTION',
    transactionpurpose: 'SUBSCRIPTION',
    createdAt: Date.now(),
  });

  createAndSubmitEsewaForm(referencedata);
};

export const startEsewaAddSlotCheckout = async (plan, slotCount) => {
  const availabilityId = resolveAvailabilityId(plan);
  if (!plan?.planId || !availabilityId) {
    throw new Error('Plan details are incomplete for payment.');
  }
  const normalizedSlotCount = Number(slotCount);
  if (!Number.isInteger(normalizedSlotCount) || normalizedSlotCount <= 0) {
    throw new Error('Slot count must be a positive whole number.');
  }

  const { successUrl, failureUrl } = resolveCheckoutUrls();

  const precheckResponse = await apiService.precheckSubscription({
    transactionpurpose: 'ADDSLOT',
    planid: plan.planId,
    currency: plan.currency || 'NPR',
    slotcount: normalizedSlotCount,
  });
  const finalAmount = precheckResponse?.data?.finalamount;
  if (finalAmount === undefined || finalAmount === null || finalAmount === '') {
    throw new Error('Invalid precheck response: missing final amount.');
  }

  const response = await apiService.initEsewaPayment({
    devicetype: 'WEB',
    amount: finalAmount,
    currency: plan.currency || 'NPR',
    transactionpurpose: 'ADDSLOT',
    transactionmode: 'WA',
    planid: plan.planId,
    availabilityid: availabilityId,
    slotcount: normalizedSlotCount,
    successUrl,
    failureUrl,
  });

  const referenceid = response?.data?.referenceid;
  const referencedata = response?.data?.referencedata;

  if (!referenceid || !referencedata?.payActionUrl) {
    throw new Error('Invalid payment init response from server.');
  }

  persistEsewaContext({
    referenceid,
    referencedata,
    slotcount: String(normalizedSlotCount),
    checkoutpurpose: 'ADDSLOT',
    transactionpurpose: 'ADDSLOT',
    createdAt: Date.now(),
  });

  createAndSubmitEsewaForm(referencedata);
};
